<template>
  <div class="home" data-page="home">
    <div
      class="home__container table table--hidden" ref="table"
      :style="{
        transform: `translate3d(${this.lerpedAmount.x}px, 0, 0)`,
      }"
    >
      <div class="table__bg"></div>
      <article
        class="table__item"
        v-for="(item, i) in $store.state.homeProjects[$store.state.lang]"
        :key="i"
      >
        <router-link
          :to="`/${$store.state.lang}/${$store.state.lang === 'en' ? 'projects' : 'progetti'}${item.link}`"
        >
          <figure class="item__image">
            <img :src="item.cover" :alt="item.title">
          </figure>
          <div class="item__content">
            <h2 class="item__title">{{ item.title }}</h2>
            <ul class="item__description">
              <li
                v-html="item.text"
              >
                {{ item.text }}
              </li>
            </ul>
          </div>
        </router-link>
      </article>
    </div>
    <p class="u-hidden--from-ipadP">Swipe to discover</p>
  </div>
</template>

<script>
import { TweenMax, Power4 } from 'gsap/TweenMax';
import Hermes from '@adoratorio/hermes';
import clamp from 'lodash/clamp';

import { isMobile } from '@/scripts/utils';
import loop from '@/scripts/loop';


export default {
  name: 'home',

  data() {
    return {
      isBlocked: true,
      scrollAmount: { x: 0 },
      lerpedAmount: { x: 0 },
      scrollLimit: 0,
    };
  },

  mounted() {
    setTimeout(() => {
      this.$refs.table.classList.remove('table--hidden');
    }, 200);

    setTimeout(() => {
      this.scrollHandler = event => this.onScroll(event);

      this.hermes = new Hermes({
        mode: Hermes.MODE.VIRTUAL,
        container: this.$el,
        touchMultiplier: 4,
      });
      this.hermes.on(this.scrollHandler);

      loop.add(this.renderScroll, 'renderScroll');
    }, 1000);

    setTimeout(() => {
      this.isBlocked = false;
    }, 2000);
  },

  methods: {
    onScroll(event) {
      if (this.isBlocked) return;

      const space = window.innerWidth < 768 ? 40 : 360;
      this.scrollLimit = this.$refs.table.getBoundingClientRect().width - window.innerWidth + space;
      const scroll = isMobile() ? -event.delta.x : -event.delta.y || -event.delta.x;
      this.scrollAmount.x = clamp(
        this.scrollAmount.x += scroll,
        -this.scrollLimit,
        0,
      );
    },

    renderScroll() {
      TweenMax.killTweensOf(this.lerpedAmount, { x: true });
      TweenMax.to(this.lerpedAmount, 1, {
        x: this.scrollAmount.x,
        ease: Power4.easeOut,
      });
    },
  },

  beforeDestroy() {
    this.hermes.destroy();
    loop.remove('renderScroll');
  },
};
</script>

<style lang="scss" scoped>
@import "~sass-mq/mq";
@import '../style/colors';
@import '../style/variables';
@import '../style/functions';
@import '../style/mixins';


.home {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  padding: 80px 0 0 20px;
  box-sizing: border-box;

  display: flex;
  align-items: center;
  justify-content: flex-start;
  flex-flow: row wrap;

  overflow: hidden;

  @include mq(ipadP) {
    padding: 80px 0 0 185px;
  }

  .table__item {
    display: flex;
    align-items: stretch;
    justify-content: stretch;

    margin-right: 80px;

    > a {
      display: flex;
      align-items: flex-start;
      flex-flow: column nowrap;

      cursor: pointer;
    }

    ul {
      position: relative;
      padding: 0 0 0 30px;

      &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        width: 1px;
        height: calc(100% - 5px);

        background-color: rgba($color-text, 0.6);
      }
    }

    li {
      line-height: 1.4rem;
    }

    &:last-child {
      margin-right: 0;
    }

    &:nth-child(odd) {
      .item__title {
        margin-top: 30px;
      }
    }

    &:nth-child(even) {
      align-self: flex-end;

      .item__image {
        order: 2;
      }

      .item__content {
        order: 1;
      }

      .item__description {
        margin-bottom: 30px;
      }
    }
  }

  .table {
    position: relative;
    width: auto;
    height: 65vh;
    padding: 40px;

    box-sizing: border-box;

    display: flex;
    align-items: stretch;
    justify-content: flex-start;
    flex-wrap: row wrap;

    @include mq(ipadP) {
      height: 70vh;
    }

    &__bg {
      position: absolute;
      top: 0;
      left: 0;
      height: 100%;
      width: 100%;

      box-shadow: 0px 0px 85px 30px rgba(0, 0, 0, 0.1);
      transition: box-shadow 1s ease-in-out,
        transform 1s ease-in-out,
        width 2s ease-in-out 1s;
      transform: scale(1);
      transform-origin: center center;
    }
  }

  .item__content {
    opacity: 1;
    transform: translateY(0);
    transition: transform 0.6s ease-in-out 1.2s,
      opacity 0.6s ease-in-out 1.2s;
  }

  .item__image {
    clip-path: inset(0 0 0 0);
    transform: translateY(0);
    transition: clip-path 0.6s ease-in-out 1.2s,
      transform 0.6s ease-in-out 1.2s;
    overflow: hidden;

    img {
      height: 32vh;

      @include mq(ipadP) {
        width: unset;
        height: 40vh;
      }
    }
  }

  .item__title {
    font-size: font-size(xsl);
    text-transform: uppercase;
    margin-bottom: 15px;

    @include mq(dm) {
      font-size: font-size(xsl);
      color: transparent;
      -webkit-text-stroke: 2px $color-text;
      letter-spacing: 2px;
    }
  }

  .item__content {
    font-size: font-size(m);

    @include mq(dm) {
      font-size: font-size(s);
    }
  }

  .table.table--hidden {
    .table__bg {
      width: calc(100vw - 370px);
      box-shadow: 0px 0px 85px 30px rgba(0, 0, 0, 0);
      transform: scale(0.95);
    }

    .table__item:nth-child(even) {
      .item__content {
        opacity: 0;
        transform: translateY(-20px);
      }

      .item__image {
        transform: translateY(20px);
        clip-path: inset(100% 0 0 0);
      }
    }

    .table__item:nth-child(odd) {
      .item__content {
        opacity: 0;
        transform: translateY(20px);
      }

      .item__image {
        transform: translateY(-20px);
        clip-path: inset(0 0 100% 0);
      }
    }
  }
}
</style>
